import { useCallback, useMemo } from 'react';
import { uniqBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/redux';

import {
  ContinueMessage,
  CustomButton,
  PageTransitionWrapper,
  StickyPanel,
} from 'src/components';
import { Box, Stack, Typography } from '@mui/material';
import { BackButton } from './components/BackButton';
import { Subheader } from './components/Subheader';

import {
  confirmCrucialRequirements,
  setCrucialRequirement,
  userSelectors,
} from 'src/redux/user';

import {
  getUserMissingRequirementCatagories,
  getUserMissingRequirementsByCategory,
} from 'src/utils';
import { JobOpeningCrucialRequirementCategory } from 'src/types';
import { AppRouteByRequirementCategory } from 'src/constants';
import {
  NONE_OPTION_VALUE,
  RequirementsList,
} from './components/RequirementsList';

const HeaderByCategory: Record<JobOpeningCrucialRequirementCategory, string> = {
  [JobOpeningCrucialRequirementCategory.DomainKnowledge]:
    'Have you worked with any of the following?',
  [JobOpeningCrucialRequirementCategory.Technology]:
    'Have you worked with any of the following?',
  [JobOpeningCrucialRequirementCategory.Education]:
    'What education do you have?',
  [JobOpeningCrucialRequirementCategory.Visa]:
    'Do you have any of the following visas?',
  [JobOpeningCrucialRequirementCategory.WorkingConditions]:
    'Are you comfortable working under these conditions?',
};

// Helper to check if category is Tech or Domain
const isTechOrDomainCategory = (
  category: JobOpeningCrucialRequirementCategory,
) =>
  category === JobOpeningCrucialRequirementCategory.Technology ||
  category === JobOpeningCrucialRequirementCategory.DomainKnowledge;

// Helper to get selected items from a record
const getSelectedFromRecord = (
  record: Record<string, boolean | null>,
): string[] =>
  Object.entries(record)
    .filter(([key, value]) => value === true && key !== NONE_OPTION_VALUE)
    .map(([key]) => key);

export const CrucialRequirements: React.FC<{
  category: JobOpeningCrucialRequirementCategory;
}> = ({ category }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const userData = useAppSelector(userSelectors.getUserData);
  const isLoading = useAppSelector(userSelectors.getIsUserPerformingAction);
  const crucialRequirements = useAppSelector(
    userSelectors.getUserCrucialRequirements,
  );

  const missingCrucialRequirements = useMemo(
    () => getUserMissingRequirementsByCategory(userData),
    [userData],
  );

  const missingCrucialRequirementCategories = useMemo(
    () => getUserMissingRequirementCatagories(userData),
    [userData],
  );

  const options = useMemo(() => {
    if (category === JobOpeningCrucialRequirementCategory.Technology) {
      // Combine Technology and DomainKnowledge requirements
      const techReqs = missingCrucialRequirements[
        JobOpeningCrucialRequirementCategory.Technology
      ].map((req) => ({
        ...req,
        category: JobOpeningCrucialRequirementCategory.Technology,
      }));

      const domainReqs = missingCrucialRequirements[
        JobOpeningCrucialRequirementCategory.DomainKnowledge
      ].map((req) => ({
        ...req,
        category: JobOpeningCrucialRequirementCategory.DomainKnowledge,
      }));

      return uniqBy([...techReqs, ...domainReqs], (req) => req.name);
    }

    return uniqBy(
      missingCrucialRequirements[category].map((req) => ({
        ...req,
        category,
      })),
      (req) => req.name,
    );
  }, [missingCrucialRequirements, category]);

  const handleConfirm = useCallback(() => {
    const isLastCategory =
      category === missingCrucialRequirementCategories.at(-1) ||
      missingCrucialRequirementCategories.length === 1;

    if (isLastCategory) {
      // Submit all requirements
      dispatch(
        confirmCrucialRequirements({
          additionalSkills: getSelectedFromRecord(
            crucialRequirements.technology,
          ),
          educations: getSelectedFromRecord(crucialRequirements.education),
          domains: getSelectedFromRecord(crucialRequirements.domainKnowledge),
          visas: getSelectedFromRecord(crucialRequirements.visa),
          workingConditions: getSelectedFromRecord(
            crucialRequirements.workingConditions,
          ),
        }),
      );
    } else {
      // Navigate to next category
      let currentCategoryIdx = missingCrucialRequirementCategories.findIndex(
        (reqCategory) => reqCategory === category,
      );

      // Handle edge case: if Technology category not found, use DomainKnowledge index
      if (
        category === JobOpeningCrucialRequirementCategory.Technology &&
        currentCategoryIdx === -1
      ) {
        currentCategoryIdx = missingCrucialRequirementCategories.findIndex(
          (reqCategory) =>
            reqCategory ===
            JobOpeningCrucialRequirementCategory.DomainKnowledge,
        );
      }

      const nextCategory =
        missingCrucialRequirementCategories[currentCategoryIdx + 1];
      navigate(`/${AppRouteByRequirementCategory[nextCategory]}`);
    }
  }, [
    category,
    crucialRequirements,
    missingCrucialRequirementCategories,
    dispatch,
    navigate,
  ]);

  const handleChange = useCallback(
    (
      reqCategory: JobOpeningCrucialRequirementCategory,
      name: string,
      value: boolean,
    ) => {
      // Find if the changed option has a description
      const changedOption = options.find((opt) => opt.name === name);
      const hasDescription = changedOption?.description;

      if (isTechOrDomainCategory(reqCategory)) {
        // Handle Technology/DomainKnowledge categories together
        const techMap = { ...crucialRequirements.technology };
        const domainMap = { ...crucialRequirements.domainKnowledge };

        // Handle "None" option logic FIRST
        if (name === NONE_OPTION_VALUE) {
          techMap[NONE_OPTION_VALUE] = value;
          domainMap[NONE_OPTION_VALUE] = value;

          if (value) {
            // Clear all other selections when "None" is selected
            Object.keys(techMap).forEach((key) => {
              if (key !== NONE_OPTION_VALUE) techMap[key] = false;
            });
            Object.keys(domainMap).forEach((key) => {
              if (key !== NONE_OPTION_VALUE) domainMap[key] = false;
            });
          }
        } else {
          // Update the appropriate map for non-None options
          if (reqCategory === JobOpeningCrucialRequirementCategory.Technology) {
            techMap[name] = value;
          } else {
            domainMap[name] = value;
          }

          // If selecting an option WITHOUT description, deselect "None"
          if (value && !hasDescription) {
            techMap[NONE_OPTION_VALUE] = false;
            domainMap[NONE_OPTION_VALUE] = false;
          }
        }

        dispatch(
          setCrucialRequirement({
            ...crucialRequirements,
            technology: techMap,
            domainKnowledge: domainMap,
          }),
        );
      } else {
        // Handle other categories (Education, Visa)
        const categoryMap = { ...crucialRequirements[reqCategory] };

        // Handle "None" option logic FIRST
        if (name === NONE_OPTION_VALUE) {
          categoryMap[NONE_OPTION_VALUE] = value;

          if (value) {
            // Clear all other selections when "None" is selected
            Object.keys(categoryMap).forEach((key) => {
              if (key !== NONE_OPTION_VALUE) categoryMap[key] = false;
            });
          }
        } else {
          // Update the specific option
          categoryMap[name] = value;

          // If selecting an option WITHOUT description, deselect "None"
          if (value && !hasDescription) {
            categoryMap[NONE_OPTION_VALUE] = false;
          }
        }

        dispatch(
          setCrucialRequirement({
            ...crucialRequirements,
            [reqCategory]: categoryMap,
          }),
        );
      }
    },
    [crucialRequirements, dispatch, options],
  );

  const selectedRequirements = useMemo(() => {
    if (isTechOrDomainCategory(category)) {
      // Combine technology and domain selections for display
      const combined: Record<string, boolean | null> = {};
      const optionNames = new Set(options.map((opt) => opt.name));

      // Add technology selections
      Object.entries(crucialRequirements.technology).forEach(([key, value]) => {
        if (optionNames.has(key) || key === NONE_OPTION_VALUE) {
          combined[key] = value;
        }
      });

      // Add domain selections
      Object.entries(crucialRequirements.domainKnowledge).forEach(
        ([key, value]) => {
          if (optionNames.has(key) || key === NONE_OPTION_VALUE) {
            combined[key] = value;
          }
        },
      );

      return combined;
    }

    return crucialRequirements[category];
  }, [crucialRequirements, category, options]);

  const isDisabled = useMemo(() => {
    let hasUnansweredWithDescription = false;
    let hasSelectionWithoutDescription = false;
    let hasOptionsWithoutDescription = false;
    const hasNoneSelected = selectedRequirements[NONE_OPTION_VALUE] === true;

    for (const option of options) {
      const value = selectedRequirements[option.name];

      if (option.description) {
        // Options with descriptions must be answered (true or false, not null/undefined)
        if (value === null || value === undefined) {
          hasUnansweredWithDescription = true;
          break;
        }
      } else {
        // Track if any non-description option is selected
        hasOptionsWithoutDescription = true;
        if (value === true) {
          hasSelectionWithoutDescription = true;
        }
      }
    }

    // Disabled if:
    // 1. Any option with description is unanswered, OR
    // 2. No selections made (neither regular options nor "None")
    return (
      hasUnansweredWithDescription ||
      (hasOptionsWithoutDescription &&
        !hasSelectionWithoutDescription &&
        !hasNoneSelected)
    );
  }, [options, selectedRequirements]);

  const hasOptionsWithoutDescription = options.some((opt) => !opt.description);

  return (
    <PageTransitionWrapper>
      <Box display="flex" flexDirection="column" gap={4}>
        <Box display="flex">
          <BackButton
            category={category}
            missingCrucialRequirementCategories={
              missingCrucialRequirementCategories
            }
          />
          <Typography variant="h2" marginBottom={0}>
            {HeaderByCategory[category]}
          </Typography>
        </Box>

        {hasOptionsWithoutDescription && <Subheader category={category} />}

        <RequirementsList
          requirements={options}
          selectedRequirements={selectedRequirements}
          handleChange={handleChange}
        />

        <StickyPanel>
          <Stack direction="row" spacing={2} alignItems="center">
            <CustomButton
              isLoading={isLoading}
              isDisabled={isDisabled}
              onClick={handleConfirm}
              label="Confirm"
            />
            <ContinueMessage textIdentifier={1} />
          </Stack>
        </StickyPanel>
      </Box>
    </PageTransitionWrapper>
  );
};
